import { useRef, useState } from 'react';
import { emptyLocalProgress } from '@/core/required-videos-storage';
import type { VideoProgress } from '@/types/required-video';
import {
  type Mode,
  type Refs,
  makeFlush,
  makeSetter,
  useInitialFetch,
  usePersistOnHide,
} from './use-required-video-progress-internals';
import { useLoadGuard } from './use-required-video-load-guard';

type Args = { videoKey: string; mode: Mode };

type Returned = {
  progress: VideoProgress;
  setProgress: (p: VideoProgress) => void;
  flush: () => void;
  progressLoading: boolean;
};

export function useRequiredVideoProgress(args: Args): Returned {
  const [progress, setLocal] = useState<VideoProgress>(() =>
    emptyLocalProgress(args.videoKey)
  );
  const refs: Refs = {
    timer: useRef(0),
    dirty: useRef<VideoProgress | null>(null),
  };
  const flushRef = useRef<() => void>(() => undefined);
  const guard = useLoadGuard(args.videoKey, setLocal);
  useInitialFetch(args.videoKey, args.mode, guard.onLoaded);
  flushRef.current = makeFlush(refs, args.mode);
  usePersistOnHide(flushRef);
  const setProgress = makeSetter(refs, setLocal, flushRef);
  return {
    progress,
    setProgress,
    flush: flushRef.current,
    progressLoading: guard.progressLoading,
  };
}
